import { useEffect, useState } from 'react';
import { api, type Mailbox } from '../lib/api.js';
import { normalizeAddress } from '../lib/front.js';
import { Button, Section } from './ui.js';

interface Props {
  current: string[];
  detected: string | null;
  onSave: (addresses: string[]) => void;
  onClose: () => void;
}

function isEmail(value: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
}

/**
 * Reglage des adresses surveillees.
 *
 * L'adresse du compte Front ne correspond pas toujours a celle qui recoit les
 * mails cote Mailinblack, et certains collaborateurs en gerent plusieurs (boite
 * nominative + boite partagee). On laisse donc en declarer autant que voulu ;
 * le panneau propose ensuite une liste deroulante pour passer de l'une a l'autre.
 */
export function MailboxSettings({ current, detected, onSave, onClose }: Props) {
  const [addresses, setAddresses] = useState<string[]>(current);
  const [draft, setDraft] = useState('');
  const [suggestions, setSuggestions] = useState<Mailbox[]>([]);

  useEffect(() => {
    const query = draft.trim();
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    // Petite temporisation : on ne sollicite pas le serveur a chaque frappe.
    const timer = setTimeout(() => {
      api
        .mailboxes(query)
        .then((result) => {
          if (!cancelled) setSuggestions(result.items.slice(0, 6));
        })
        .catch(() => setSuggestions([]));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [draft]);

  function add(address: string): string[] {
    const normalized = normalizeAddress(address);
    if (!normalized || !isEmail(normalized) || addresses.includes(normalized)) return addresses;
    const next = [...addresses, normalized];
    setAddresses(next);
    setDraft('');
    setSuggestions([]);
    return next;
  }

  /**
   * Enregistrement.
   *
   * Le champ de saisie est valide au passage : sans cela, une adresse tapee puis
   * enregistree directement — sans passer par Entree — etait silencieusement
   * perdue, et la liste ne changeait pas.
   */
  function save() {
    const pending = draft.trim();
    onSave(pending ? add(pending) : addresses);
  }

  return (
    <Section title="Mes adresses">
      <p className="muted">
        Adresses supplémentaires à surveiller
        {detected ? `, en plus de ${detected}` : ''}. Elles apparaîtront dans la liste déroulante.
      </p>

      {addresses.length > 0 && (
        <ul className="settings-list">
          {addresses.map((address) => (
            <li key={address}>
              <span>{address}</span>
              <Button variant="link" onClick={() => setAddresses(addresses.filter((item) => item !== address))}>
                retirer
              </Button>
            </li>
          ))}
        </ul>
      )}

      <input
        className="settings-input"
        type="email"
        value={draft}
        placeholder="adresse@sysaxes.com"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            add(draft);
          }
        }}
      />

      {suggestions.length > 0 && (
        <ul className="settings-suggestions">
          {suggestions.map((item) => (
            <li key={item.address}>
              <Button variant="link" onClick={() => add(item.address)}>
                {item.address}
              </Button>
              <span className="settings-count">{item.count}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="settings-actions">
        <Button variant="primary" onClick={save}>
          Enregistrer
        </Button>
        <Button onClick={onClose}>Annuler</Button>
      </div>
    </Section>
  );
}
